import React, { useState } from "react";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { CheckSquare, Copy, Info, Lightbulb, Plus, X } from "lucide-react";
import { Link } from "react-router-dom";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { toast } from "sonner";
import { Textarea } from "../../../ui/textarea";
import { useNotes } from "../../chatPanel/takeaways/use-notes";
import { useTranslation } from "react-i18next";
import { LocationObject } from "../../../../lib/types";

const ActivityCompletionModal = ({
  isDialogOpen,
  setIsDialogOpen,
  isUserSignedIn = true,
  processId,
  forwardLocation,
}: {
  isDialogOpen: boolean;
  setIsDialogOpen: (arg0: boolean) => void;
  isUserSignedIn: boolean;
  processId: string;
  forwardLocation: LocationObject;
}) => {
  const [showNewNoteForm, setShowNewNoteForm] = useState(false);
  const [newNoteType, setNewNoteType] = useState<"action" | "insight">(
    "action"
  );
  const [newNoteContent, setNewNoteContent] = useState("");
  const [selectedNoteIndex, setSelectedNoteIndex] = useState<number | null>(
    null
  );

  const { t } = useTranslation();

  const { notes, loading, addNote, editNote, removeNote } = useNotes({
    processId,
    onError: (error: any) => {
      toast(error);
    },
    onSuccess: (message: any) => {
      toast(message);
    },
  });

  const toggleNoteType = async (index: number) => {
    const note: any = notes[index];
    if (!note._id) return;

    const newType = note.type === "action" ? "insight" : "action";
    await editNote(note._id, { type: newType });
  };

  const deleteTakeAway = async (index: number) => {
    const note = notes[index];
    if (!note._id) return;

    await removeNote(note._id);
    if (selectedNoteIndex === index) {
      setSelectedNoteIndex(null);
    } else if (selectedNoteIndex !== null && selectedNoteIndex > index) {
      setSelectedNoteIndex(selectedNoteIndex - 1);
    }
  };

  const saveNoteContent = async (index: number, content: string) => {
    const note = notes[index];
    if (!note._id) return;

    if (content.trim() === "") {
      await removeNote(note._id);
    } else {
      await editNote(note._id, { content });
    }
  };

  const renderNotesList = (
    notesList: any,
    title: string,
    icon: React.ReactNode,
    emptyMessage: string
  ) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h4 className="text-sm font-medium">{title}</h4>
          <span className="text-xs text-muted-foreground">
            ({notesList.length})
          </span>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            if (title === "Insights") {
              setNewNoteType("insight");
            } else {
              setNewNoteType("action");
            }
            setShowNewNoteForm(true);
          }}
          disabled={loading}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("add")}
        </Button>
      </div>
      <div className="space-y-2 max-h-[10rem] overflow-y-auto pr-1">
        {notesList.map((note: any) => {
          const noteIndex = notes.findIndex((n) => n._id === note._id);
          return (
            <div
              key={note._id}
              className="flex items-center gap-2 p-2 rounded-md border group hover:bg-muted/50"
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 mt-1"
                onClick={() => toggleNoteType(noteIndex)}
                title={note.type === "action" ? "Action Item" : "Insight"}
                disabled={loading}
              >
                {note.type === "action" ? (
                  <CheckSquare className="h-4 w-4 text-blue-500" />
                ) : (
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                )}
              </Button>

              <div className="flex-1 space-y-2">
                {selectedNoteIndex === noteIndex ? (
                  <Textarea
                    defaultValue={note.content}
                    className="resize-none min-h-[60px]"
                    autoFocus
                    onBlur={(e) => {
                      setSelectedNoteIndex(null);
                      saveNoteContent(noteIndex, e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        setSelectedNoteIndex(null);
                        saveNoteContent(noteIndex, e.currentTarget.value);
                      }
                    }}
                    disabled={loading}
                  />
                ) : (
                  <div
                    className="text-sm cursor-pointer"
                    onClick={() => setSelectedNoteIndex(noteIndex)}
                  >
                    {note.content}
                  </div>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => deleteTakeAway(noteIndex)}
                title="Delete note"
                disabled={loading}
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          );
        })}

        {notesList.length === 0 && (
          <div className="text-center py-3 text-muted-foreground">
            <p className="text-sm">{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );

  const copyTakeawaysToClipboard = async () => {
    const actionItems = notes
      .filter(
        (note: any) => note.type === "action" && note.content.trim() !== ""
      )
      .map((note: any) => `- ${note.content}`);

    const insights = notes
      .filter(
        (note: any) => note.type === "insight" && note.content.trim() !== ""
      )
      .map((note: any) => `- ${note.content}`);

    let notesText = "";

    if (actionItems.length > 0) {
      notesText += `📝 Action Items:\n${actionItems.join("\n")}\n\n`;
    }

    if (insights.length > 0) {
      notesText += `💡 Insights:\n${insights.join("\n")}`;
    }

    if (!notesText.trim()) {
      toast.warning("No notes to copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(notesText.trim());
      toast.info("Copied Take-Aways to Clipboard!");
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      toast.error("Failed to copy notes");
    }
  };

  const handleCreateNote = async () => {
    if (!newNoteContent.trim()) return;

    const success = await addNote({
      type: newNoteType,
      content: newNoteContent,
      createdBy: "current-user",
    });

    if (success) {
      setNewNoteContent("");
      setShowNewNoteForm(false);
    }
  };

  const actionNotes = notes.filter((note: any) => note.type === "action");
  const insightNotes = notes.filter((note: any) => note.type === "insight");

  function computeForwardLocationURL() {
    if (forwardLocation.pathname && forwardLocation.search) {
      return `${forwardLocation.pathname}${forwardLocation.search}`;
    } else {
      return forwardLocation.pathname;
    }
  }

  const continueTo = computeForwardLocationURL();

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent className="max-w-md md:max-w-xl xl:max-w-2xl max-h-screen overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("engagement_completed_modal_title")}!</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isUserSignedIn ? (
            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckSquare className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <p className="font-medium mb-1">
                  {t("engagement_saved_heading")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("engagement_saved_description")}
                </p>
              </div>
              {notes.filter((note) => note.content.trim() !== "").length >
                0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">
                      {t("your_takeaways")}
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyTakeawaysToClipboard}
                      className="flex items-center gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      {t("copy_all_notes")}
                    </Button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Actions Section */}
                    <div className="space-y-3">
                      {!showNewNoteForm &&
                        renderNotesList(
                          actionNotes,
                          t("action_items"),
                          <CheckSquare className="h-4 w-4 text-blue-500" />,
                          t("no_action_items_yet")
                        )}
                    </div>

                    {/* Insights Section */}
                    <div className="space-y-3">
                      {!showNewNoteForm &&
                        renderNotesList(
                          insightNotes,
                          t("insights"),
                          <Lightbulb className="h-4 w-4 text-amber-500" />,
                          t("no_insights_yet")
                        )}
                    </div>
                  </div>

                  {/* New Note Form */}
                  {showNewNoteForm && (
                    <div className="border rounded-md p-3 space-y-3 bg-muted/20">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0"
                          onClick={() => {
                            setNewNoteType(
                              newNoteType === "action" ? "insight" : "action"
                            );
                          }}
                        >
                          {newNoteType === "action" ? (
                            <CheckSquare className="h-4 w-4 text-blue-500" />
                          ) : (
                            <Lightbulb className="h-4 w-4 text-amber-500" />
                          )}
                        </Button>
                        <div className="text-sm font-medium">
                          {newNoteType === "action"
                            ? t("action_item")
                            : t("insight")}
                        </div>
                      </div>

                      <Textarea
                        placeholder={t("new_note_field_placeholder")}
                        value={newNoteContent}
                        onChange={(e) => setNewNoteContent(e.target.value)}
                        className="resize-none min-h-[80px]"
                        autoFocus
                      />

                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowNewNoteForm(false);
                            setNewNoteContent("");
                          }}
                          disabled={loading}
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleCreateNote}
                          disabled={!newNoteContent.trim() || loading}
                        >
                          {loading ? t("saving") : t("save")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <Link to={continueTo}>
                <Button className="w-full mt-3">Continue</Button>
              </Link>
            </div>
          ) : (
            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                <Info className="h-8 w-8 text-yellow-600" />
              </div>
              <div>
                <p className="font-medium mb-1 text-yellow-800">
                  {t("engagement_completed_heading_for_non_logged_in_user")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("engagement_completed_description_for_non_logged_in_user")}
                </p>
              </div>
              {notes.filter((note) => note.content.trim() !== "").length >
                0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">
                      {t("your_takeaways")}
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyTakeawaysToClipboard}
                      className="flex items-center gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      {t("copy_all_notes")}
                    </Button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Actions Section */}
                    <div className="space-y-3">
                      {!showNewNoteForm &&
                        renderNotesList(
                          actionNotes,
                          t("action_items"),
                          <CheckSquare className="h-4 w-4 text-blue-500" />,
                          t("no_action_items_yet")
                        )}

                      {!showNewNoteForm && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            setNewNoteType("action");
                            setShowNewNoteForm(true);
                          }}
                          disabled={loading}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t("add_action_item")}
                        </Button>
                      )}
                    </div>

                    {/* Insights Section */}
                    <div className="space-y-3">
                      {!showNewNoteForm &&
                        renderNotesList(
                          insightNotes,
                          t("insights"),
                          <Lightbulb className="h-4 w-4 text-amber-500" />,
                          t("no_insights_yet")
                        )}

                      {!showNewNoteForm && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            setNewNoteType("insight");
                            setShowNewNoteForm(true);
                          }}
                          disabled={loading}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t("add_insight")}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* New Note Form */}
                  {showNewNoteForm && (
                    <div className="border rounded-md p-3 space-y-3 bg-muted/20">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0"
                          onClick={() => {
                            setNewNoteType(
                              newNoteType === "action" ? "insight" : "action"
                            );
                          }}
                        >
                          {newNoteType === "action" ? (
                            <CheckSquare className="h-4 w-4 text-blue-500" />
                          ) : (
                            <Lightbulb className="h-4 w-4 text-amber-500" />
                          )}
                        </Button>
                        <div className="text-sm font-medium">
                          {newNoteType === "action" ? "Action Item" : "Insight"}
                        </div>
                      </div>

                      <Textarea
                        placeholder="Enter your note here..."
                        value={newNoteContent}
                        onChange={(e) => setNewNoteContent(e.target.value)}
                        className="resize-none min-h-[80px]"
                        autoFocus
                      />

                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowNewNoteForm(false);
                            setNewNoteContent("");
                          }}
                          disabled={loading}
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleCreateNote}
                          disabled={!newNoteContent.trim() || loading}
                        >
                          {loading ? t("saving") : t("save")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Button className="w-full">{t("login")}</Button>
                <Button variant="outline" className="w-full">
                  {t("register_new_user")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityCompletionModal;
